// @ts-nocheck

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FiArrowLeft, FiCalendar, FiMapPin, FiUser, FiX as Twitter } from "react-icons/fi";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { routes } from "../utils/utils.routes";
import { PageSectionHeader } from "./subcomponents/PageSectionHeader";
import { BreadcrumbArticle } from "./subcomponents/BreadcrumpArticle";
import { SocialShare } from "./subcomponents/Sharesocial";
import { truncateText } from "../utils/utils.fucntions";
import { getContentDetailConfig } from "../utils/contentDetail";
import { getContentMediaAttachment } from "../utils/contentMedia";
import { ContentMediaPreview } from "./subcomponents/ContentMediaPreview";
import { apiGet } from "../utils/api";

const BackI = FiArrowLeft as any;
const CalI = FiCalendar as any;
const UserI = FiUser as any;
const MapI = FiMapPin as any;
const FiX = Twitter as any;

type Props = { item: any; slug: string };

export const ArticleDetailView: React.FC<Props> = ({ item, slug }) => {
  const cfg = getContentDetailConfig(item);
  const media = getContentMediaAttachment(item);
  const published = new Date(item.publishedAt || item.createdAt).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const showLocation = item.location && !/^https?:\/\//i.test(item.location);
  const gallery: string[] = Array.isArray(item.galleryImages) ? item.galleryImages.filter(Boolean) : [];

  // --- Balises Open Graph ---
  const siteUrl = "https://ulpgl.net";
  const pageUrl = `${siteUrl}/article/${slug}`;
  const ogImage = item.coverImage || gallery[0] || `${siteUrl}/og-default.jpg`;
  const ogDescription = item.excerpt
    ? truncateText(item.excerpt, 160)
    : "Lisez cet article sur ULPGL - Université Libre des Pays des Grands Lacs";

  // Lightbox state (inchangé)
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [similar, setSimilar] = useState<any[]>([]);

  useEffect(() => {
    if (!item.category) return;
    apiGet("/contents", { category: item.category, limit: 5 })
      .then((d: any) => {
        const rows = (d.rows || []).filter((r: any) => r.id !== item.id);
        setSimilar(rows.slice(0, 4));
      })
      .catch(() => {});
  }, [item.category, item.id]);

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "";
  };

  // eslint-disable-next-line 
  const goPrev = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? gallery.length - 1 : prev - 1));
  };

  // eslint-disable-next-line 
  const goNext = () => {
    setCurrentImageIndex((prev) => (prev === gallery.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line 
  }, [lightboxOpen, currentImageIndex, goNext, goPrev]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <>
      <Helmet>
        {/* Balises de base */}
        <title>{item.title} | ULPGL</title>
        <meta name="description" content={ogDescription} />

        {/* Open Graph (Facebook, WhatsApp, LinkedIn) */}
        <meta property="og:title" content={item.title} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="ULPGL - Goma" />

        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={item.title} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImage} />

        {/* Optionnel : image sécurisée HTTPS */}
        <meta property="og:image:secure_url" content={ogImage} />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>

      <div className="content-detail padding-x is-layout-constrained" data-testid="article-detail">
        <Link to={cfg.backHref} className="content-detail__back pt-3">
          <BackI size={16} /> {cfg.backLabel}
        </Link>

        <BreadcrumbArticle
          category={item.category ?? item.type}
          post_title={truncateText(item.title ?? "", 60)}
          type={item.type}
        />

        <PageSectionHeader
          eyebrow={cfg.eyebrow}
          title={item.title}
          lead={item.excerpt}
          align="start"
          headingLevel="h1"
        />

        <div className="content-detail__hero-meta">
          <span className="content-detail__badge">{cfg.typeBadge}</span>
          <span><UserI size={14} /> {item.author?.name || "ULPGL"}</span>
          <span><CalI size={14} /> Publié le {published}</span>
          {item.eventDate && (
            <span>
              <CalI size={14} /> {item.type === "event" ? "Date de l'événement" : "Date"} :{" "}
              {new Date(item.eventDate).toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
          {showLocation && (
            <span><MapI size={14} /> {item.location}</span>
          )}
        </div>

        <div className="content-detail__layout">
          <div className="content-detail__main">
            {item.coverImage && (
              <img src={item.coverImage} alt="" className="content-detail__cover" />
            )}
            {media && (
              <div className="content-detail__media my-4" data-testid="article-media-attachment">
                <ContentMediaPreview media={media} />
              </div>
            )}
            {item.excerpt && (
              <p className="content-detail__excerpt">{item.excerpt}</p>
            )}
            <div className="rich-content" dangerouslySetInnerHTML={{ __html: item.content || "" }} />

            {gallery.length > 0 && (
              <div className="content-detail__gallery" data-testid="article-gallery">
                <PageSectionHeader
                  eyebrow={"Gallérie"}
                  title={"Gallérie d'images"}
                  align="start"
                  headingLevel="h1"
                />
                <div className="gallery-grid">
                  {gallery.map((src, idx) => (
                    <div key={idx} className="gallery-item" onClick={() => openLightbox(idx)}>
                      <img src={src} alt={`${idx + 1}`} loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="content-detail__rail">
            <div className="content-detail__card">
              <h3>Articles similaires</h3>
              {similar.length === 0 ? (
                <p className="content-detail__muted" style={{ fontSize: 13 }}>Aucun article similaire</p>
              ) : (
                <ul className="content-detail__similar">
                  {similar.map((s: any) => (
                    <li key={s.id}>
                      <Link to={`${routes.ARTICLESDETAILS}/${s.slug}`}>
                        {s.coverImage && (
                          <img src={s.coverImage} alt="" />
                        )}
                        <div>
                          <strong>{s.title}</strong>
                          <span>{new Date(s.publishedAt || s.createdAt).toLocaleDateString("fr-FR")}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <aside className="content-detail__aside">
            <div className="content-detail__card">
              <h3>Partager</h3>
              <SocialShare slug={slug} title={item.title} description={item.excerpt} />
            </div>

            <div className="content-detail__card content-detail__card--accent">
              <h3>Liens utiles</h3>
              <ul className="content-detail__links">
                <li><Link to={routes.ARTICLES}>Actualités</Link></li>
                <li><Link to={routes.EVENTS}>Événements</Link></li>
                <li><Link to={routes.SCHEDULES}>Horaires des cours</Link></li>
                <li><Link to={routes.TENDERS}>Appels d&apos;offres</Link></li>
                <li><Link to={routes.CONTACTS}>Nous contacter</Link></li>
              </ul>
            </div>
          </aside>
        </div>

        {lightboxOpen && (
          <div className="lightbox-overlay" onClick={closeLightbox}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Fermer">
              <FiX size={28} />
            </button>
            <button className="lightbox-prev" onClick={(e) => { e.stopPropagation(); goPrev(); }} aria-label="Image précédente">
              <ChevronLeft size={40} />
            </button>
            <button className="lightbox-next" onClick={(e) => { e.stopPropagation(); goNext(); }} aria-label="Image suivante">
              <ChevronRight size={40} />
            </button>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={gallery[currentImageIndex]} alt={`Galerie ${currentImageIndex + 1}`} />
              <div className="lightbox-counter">
                {currentImageIndex + 1} / {gallery.length}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};